'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { claimPromoSlot, releasePromoSlot, isOrderFlowRoute, popupStorageGet, popupStorageSet } from '@/lib/ui/popupQueue';
import OfferDialog, { OFFER_SUBSCRIBED_KEY, useShopperAudience, type PopupCoupon } from '@/components/ecommerce/OfferDialog';

const POPUP_ID = 'email-capture';
interface EmailCapturePopupProps {
  discountPercent?: number;
  discountCode?: string;
  delaySeconds?: number;
  storageKey?: string;
}

/** A scroll-triggered offer banner. The signup dialog opens only on a deliberate tap. */
export default function EmailCapturePopup({ delaySeconds, storageKey = 'email-capture-shown' }: EmailCapturePopupProps) {
  const pathname = usePathname();
  const shopper = useShopperAudience();
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [coupon, setCoupon] = useState<PopupCoupon | null>(null);
  const [settings, setSettings] = useState<{ enabled: boolean; delay: number } | null>(null);
  const banner = useRef<HTMLDivElement>(null);
  const dismissed = useRef(false);
  const seen = useCallback(() => popupStorageGet(storageKey) !== null || popupStorageGet(OFFER_SUBSCRIBED_KEY) !== null, [storageKey]);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetch('/api/v2/commerce/popup-settings', { signal: controller.signal }).then(r => r.ok ? r.json() : null),
      fetch('/api/v2/commerce/homepage-popup-coupon', { signal: controller.signal }).then(r => r.ok ? r.json() : null),
    ]).then(([config, promotion]) => {
      if (controller.signal.aborted) return;
      const activeCoupon = promotion?.data?.coupon;
      // Fail closed: never advertise an invented discount if settings or coupon loading fails.
      if (!config?.success || !activeCoupon?.code) return;
      setCoupon(activeCoupon);
      setSettings({ enabled: config.data?.enable_email_capture_popup === true || config.data?.enable_email_capture_popup === 1,
        delay: Math.max(0, Number(delaySeconds ?? config.data?.email_capture_delay_seconds ?? 10)) });
    }).catch(() => {});
    return () => controller.abort();
  }, [delaySeconds]);

  const close = useCallback(() => {
    dismissed.current = true;
    popupStorageSet(storageKey, String(Date.now()));
    setVisible(false);
    setDialogOpen(false);
    releasePromoSlot(POPUP_ID);
  }, [storageKey]);

  useEffect(() => {
    setVisible(false);
    setDialogOpen(false);
    releasePromoSlot(POPUP_ID);
    if (!settings?.enabled || !coupon || shopper !== true || seen() || isOrderFlowRoute(pathname)) return;
    let delayPassed = false;
    const attempt = () => {
      if (!delayPassed || dismissed.current || seen() || isOrderFlowRoute() || document.visibilityState !== 'visible') return;
      if (window.scrollY < 240) return;
      if (!claimPromoSlot(POPUP_ID)) return;
      popupStorageSet(storageKey, String(Date.now()));
      setVisible(true);
    };
    const timer = setTimeout(() => { delayPassed = true; attempt(); }, settings.delay * 1000);
    window.addEventListener('scroll', attempt, { passive: true });
    return () => { clearTimeout(timer); window.removeEventListener('scroll', attempt); releasePromoSlot(POPUP_ID); };
  }, [pathname, settings, coupon, shopper, seen, storageKey]);

  useEffect(() => {
    const node = banner.current;
    if (!visible || dialogOpen || !node) return;
    const update = () => document.documentElement.style.setProperty('--offer-bar-h', `${node.getBoundingClientRect().height + 16}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => { observer.disconnect(); document.documentElement.style.removeProperty('--offer-bar-h'); };
  }, [visible, dialogOpen]);

  if (!visible || !coupon || shopper !== true || isOrderFlowRoute(pathname)) return null;
  const offer = coupon.discountPercent == null ? `$${coupon.discountValue} off` : `${coupon.discountPercent}% off`;
  if (dialogOpen) return <OfferDialog title="Your new customer offer" source="popup" coupon={coupon}
    fallbackPercent={coupon.discountPercent ?? 0} fallbackCode={coupon.code} onClose={close} onSubscribed={() => popupStorageSet(storageKey, String(Date.now()))} />;

  return <div ref={banner} role="region" aria-label="Optional new customer offer"
    className="fixed inset-x-3 z-40 mx-auto flex max-w-xl items-center gap-3 rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-lg"
    style={{ bottom: 'calc(8px + max(var(--sticky-bar-h, 0px), var(--config-bar-h, 0px)))' }}>
    <p className="m-0 min-w-0 flex-1 text-sm leading-snug"><strong>Get {offer} by email</strong><br />Offer terms apply.</p>
    <button type="button" className="min-h-11 shrink-0 rounded-lg bg-gray-900 px-3 text-sm font-semibold text-white" onClick={() => setDialogOpen(true)}>View offer</button>
    <button type="button" aria-label="Dismiss offer" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-gray-100" onClick={close}><X size={20} /></button>
  </div>;
}
